import {
    CheckCircle as CheckCircleIcon,
    DoneRounded as DoneRoundedIcon,
    ErrorRounded as ErrorRoundedIcon,
} from 'components/icons';
import { Progress as ProgressPrimitive } from '@base-ui/react/progress';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import PermissionButton from 'components/buttons/PermissionButton';
import CircularLoader from 'components/CircularLoader';
import { Alert } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Card, CardContent } from 'components/ui/card';
import { ProgressIndicator, ProgressTrack } from 'components/ui/progress';
import { ProcessType, useRunProcessMutation } from 'store/api/processes';
import { cn } from 'utils/cn';

const COMPLETION_CHIP_DISMISS_MS = 10000;

export interface ProcessCardConfig {
    type: ProcessType;
    titleId: string;
    descriptionId: string;
    runButtonId?: string;
    inProgressLabelId?: string;
    progressMessageId?: string;
    successChipLabelId?: string;
    successAlertId?: string;
    errorGeneralId?: string;
    errorAlreadyRunningId?: string;
    buttonVariant?: 'contained' | 'outlined';
    buttonColor?: 'primary' | 'secondary';
}

const DEFAULT_RUN_BUTTON_ID = 'processes.run-button';
const DEFAULT_IN_PROGRESS_LABEL_ID = 'processes.status-in-progress';
const DEFAULT_PROGRESS_MESSAGE_ID = 'processes.progress-message';
const DEFAULT_SUCCESS_CHIP_LABEL_ID = 'processes.status-completed';
const DEFAULT_SUCCESS_ALERT_ID = 'processes.success';
const DEFAULT_ERROR_GENERAL_ID = 'processes.error-general';
const DEFAULT_ERROR_ALREADY_RUNNING_ID = 'processes.error-already-running';

interface Props {
    config: ProcessCardConfig;
    isRunning: boolean;
}

/** A spinner sized to sit inline with a line of text. */
export const INLINE_SPINNER_CLASS = 'static size-auto [&>span]:size-4 [&>span]:border-2';

const ALERT_CLASS =
    'text-fg flex items-center gap-2 rounded-lg py-2 pr-4 pl-3 text-sm font-semibold *:[svg]:text-primary';

export default function ProcessCard({ config, isRunning }: Props) {
    const [runProcess, { isLoading, error, isSuccess, reset }] = useRunProcessMutation();

    const runButtonId = config.runButtonId ?? DEFAULT_RUN_BUTTON_ID;
    const inProgressLabelId = config.inProgressLabelId ?? DEFAULT_IN_PROGRESS_LABEL_ID;
    const progressMessageId = config.progressMessageId ?? DEFAULT_PROGRESS_MESSAGE_ID;
    const successChipLabelId = config.successChipLabelId ?? DEFAULT_SUCCESS_CHIP_LABEL_ID;
    const successAlertId = config.successAlertId ?? DEFAULT_SUCCESS_ALERT_ID;
    const errorGeneralId = config.errorGeneralId ?? DEFAULT_ERROR_GENERAL_ID;
    const errorAlreadyRunningId = config.errorAlreadyRunningId ?? DEFAULT_ERROR_ALREADY_RUNNING_ID;
    const buttonVariant = config.buttonVariant ?? 'contained';
    const buttonColor = config.buttonColor ?? 'secondary';

    const [justCompleted, setJustCompleted] = useState(false);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- previous-prop tracker pattern; read during render at `if (prevIsRunning !== isRunning)` to detect prop transitions
    const [prevIsRunning, setPrevIsRunning] = useState(isRunning);
    if (prevIsRunning !== isRunning) {
        setPrevIsRunning(isRunning);
        if (prevIsRunning && !isRunning) {
            setJustCompleted(true);
        }
    }

    // TODO(react-doctor: no-effect-event-handler): schedules a setTimeout to
    // auto-dismiss the chip; the effect owns the timer lifecycle (clearTimeout on
    // unmount), which a handler cannot do safely. Intentionally kept in an effect.
    useEffect(() => {
        if (justCompleted) {
            const timer = setTimeout(() => setJustCompleted(false), COMPLETION_CHIP_DISMISS_MS);
            return () => clearTimeout(timer);
        }
    }, [justCompleted]);

    const handleRun = useCallback(async () => {
        try {
            setJustCompleted(false);
            reset();
            await runProcess(config.type).unwrap();
        } catch (err) {
            console.error('Process run failed:', err);
        }
    }, [config.type, runProcess, reset]);

    const isAlreadyRunningError = error && 'status' in error && error.status === 409;

    return (
        <Card data-testid="process-card" className="gap-0 py-0">
            <CardContent className="flex flex-col gap-4 p-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-h6">
                        <FormattedMessage id={config.titleId} />
                    </h2>

                    {(isSuccess || justCompleted) && !isRunning && (
                        <Badge className="bg-success text-primary-foreground h-8 gap-1.5 rounded-full px-3 text-sm [&>svg]:size-5!">
                            <CheckCircleIcon />
                            <FormattedMessage id={successChipLabelId} />
                        </Badge>
                    )}
                </div>

                <p className="text-body1 text-fg-secondary">
                    <FormattedMessage id={config.descriptionId} />
                </p>

                {isRunning && (
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <CircularLoader className={INLINE_SPINNER_CLASS} />
                            <p className="text-body2 text-fg-secondary">
                                <FormattedMessage id={progressMessageId} />
                            </p>
                        </div>
                        <ProgressPrimitive.Root value={null}>
                            <ProgressTrack className="h-1.5 rounded">
                                <ProgressIndicator className="loading-bar-sweep w-full rounded transition-none" />
                            </ProgressTrack>
                        </ProgressPrimitive.Root>
                    </div>
                )}

                <div>
                    <PermissionButton
                        permissions={['RUN.PROCESSES']}
                        variant={buttonVariant}
                        color={buttonColor}
                        disabled={isRunning || isLoading}
                        onClick={() => {
                            void handleRun();
                        }}
                        startIcon={
                            isLoading ? (
                                <CircularLoader className={cn(INLINE_SPINNER_CLASS, '[&>span]:size-5')} />
                            ) : null
                        }
                    >
                        <FormattedMessage id={isRunning ? inProgressLabelId : runButtonId} />
                    </PermissionButton>
                </div>

                {error && !isRunning && (
                    <Alert className={cn(ALERT_CLASS, 'border-error bg-error-light')}>
                        <ErrorRoundedIcon />
                        <FormattedMessage id={isAlreadyRunningError ? errorAlreadyRunningId : errorGeneralId} />
                    </Alert>
                )}

                {(isSuccess || justCompleted) && !isRunning && !error && (
                    <Alert className={cn(ALERT_CLASS, 'border-success bg-success-light')}>
                        <DoneRoundedIcon />
                        <FormattedMessage id={successAlertId} />
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}
